import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CommunityLayout from "../components/layouts/CommunityLayout";
import { usePageTitle } from '../hooks/usePageTitle';
import { BlueEnvelopeProgramOptions } from '../utils';
import { getSessionData } from '../utils/auth';
import InstructionsSolid from "../components/icons/InstructionsSolid";
import FormSolidIcon from "../components/icons/FormSolid";
import EnvelopeClosedSolid from "../components/icons/EnvelopeClosedSolid";
import TableIcon from "../components/icons/Table";
import DocumentSolidIcon from "../components/icons/DocumentSolid";
import ArticleIcon from "../components/icons/ArticleIcon";

const iconMap = {
    'overview': InstructionsSolid,
    'form': FormSolidIcon,
    'envelope': EnvelopeClosedSolid,
    'card': ArticleIcon,
    'virtual_form': TableIcon,
    'other_documents': DocumentSolidIcon,
};

const BlueEnvelopeProgramLanding = () => {
    usePageTitle('Blue Envelope Program - Wefayo');
    const navigate = useNavigate();

    useEffect(() => {
        if (getSessionData() === null) {
            navigate('/login');
        }
    }, [navigate]);

    const handleSelectOption = (option) => {
        // open in a new tab
        window.open(`/blue-envelope-program/${option.module}`, '_blank');
        // navigate(`/blue-envelope-program/${option.module}`);
    };

    return (
        <CommunityLayout
            currentPage="blue-envelope-program"
            onCommunitiesLoaded={() => { }}
            isFullPageWidth={true}
            overflow="overflow-y-auto"
            px="px-0"
            mustShowFooter={true}
        >
            <div
                className="relative w-full flex pt-0 md:pt-[5px] lg:pt-[57px] h-[calc(100vh-57px)] overflow-y-auto"
                style={{ background: 'linear-gradient(135deg, #0c2340 0%, #1e3a5f 55%, #2563eb 100%)' }}
            >
                <div
                    className="absolute inset-0 pointer-events-none opacity-10"
                    style={{
                        backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
                        backgroundSize: '30px 30px',
                    }}
                />

                <div className="relative flex flex-col lg:flex-row items-start justify-between gap-10 px-6 md:px-12 lg:px-16 py-16 md:py-20 mx-auto max-w-8xl xl:max-w-[1500px]">

                    {/* LEFT — Hero copy */}
                    <div className="h-full flex flex-col items-start justify-center text-left lg:max-w-[40%] lg:sticky lg:top-8">


                        <div className="hidden lg:flex w-16 h-16 rounded-full bg-white/10 border border-white/20  items-center justify-center mb-6">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="white" className="w-8 h-8">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                            </svg>
                        </div>

                        {/* Heading */}
                        <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight flex flex-row items-start lg:items-center  ">
                            <div className="lg:hidden !w-16 !h-16 flex-shrink-0 rounded-full bg-white/10 border border-white/20 flex items-center justify-center ">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="white" className="w-8 h-8">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
                                </svg>
                            </div>
                            <div className="pl-2 lg:pl-0 ">
                                One simple tool,{' '}
                                <span className="italic text-sky-300">so every encounter is better.</span>
                            </div>
                        </h1>

                        <p className="text-white/80 text-base mb-4">
                            The Blue Envelope Program offers simple, free tools that may help
                            people with special needs communicate with first responders &mdash;
                            and help first responders understand the person&rsquo;s special communication needs.
                        </p>

                        <p className="text-white/50 text-sm">
                            All materials are free. Optional. Update and reprint anytime.
                        </p>



                        {/* Badge */}
                        <div className="mt-5 lg:mt-20 self-start inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-white/90 text-xs font-semibold mb-6 tracking-widest uppercase">
                            <span className="w-1.5 h-1.5 rounded-full bg-sky-300 inline-block" />
                            FREE &middot; VOLUNTARY &middot; PRINTABLE
                        </div>
                    </div>

                    {/* RIGHT — Option cards */}
                    <div className="flex flex-col w-full lg:max-w-[60%] items-center justify-center md:h-full">

                        <h2 className="text-xl font-bold text-white text-center mb-1">
                            Select a Program Tool to begin
                        </h2>
                        <p className="text-white/60 text-sm text-center mb-6">
                            Pick the tool that best fits your needs right now.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {BlueEnvelopeProgramOptions.map((option) => {
                                const IconComponent = iconMap[option.module] || ArticleIcon;
                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => handleSelectOption(option)}
                                        className="flex flex-col items-center p-4 md:p-5 rounded-xl border-2 border-white/20 bg-white/5 hover:bg-white/15 hover:border-sky-300 hover:shadow-lg transition-all duration-200 group text-center"
                                    >
                                        <div className="w-20 h-20 md:w-24 md:h-24 bg-white/10 rounded-full flex items-center justify-center p-2 mb-3 group-hover:bg-sky-500/40 transition-colors duration-200">
                                            <IconComponent
                                                className="w-10 h-10 md:w-14 md:h-14 text-sky-200"
                                                fillColor="#bae6fd"
                                            />
                                        </div>
                                        <h3 className="font-bold text-white text-lg mb-1">
                                            {option.name}
                                        </h3>
                                        <p className="text-md text-sky-200 font-semibold mb-2">
                                            {option.description}
                                        </p>
                                    </button>
                                );
                            })}
                        </div>

                        <p className="text-white/30 text-sm text-center mt-6 w-full mx-auto">
                            This is a voluntary communication support initiative.
                            It is informational and does not replace standard procedures or decision-making.
                            Participation does not grant special privileges.
                        </p>
                    </div>

                </div>
            </div>
        </CommunityLayout>
    );
};

export default BlueEnvelopeProgramLanding;
